/* eslint @typescript-eslint/strict-boolean-expressions: off */

import max from "lodash/max";
import min from "lodash/min";
import sum from "lodash/sum";
import { AxisOptions, ChartType, VisualChart } from "src/gql/visuals/types";
import { ChartBounds } from "src/components/chart/bounds";
import {
  ChartSeries,
  leftAxisColumns,
  rightAxisColumns,
} from "src/components/chart/series";
import { QueryResultColumn, QueryResultRow } from "src/utils/results/types";
import { ScaleBand, ScaleLinear, ScalePower, ScaleTime } from "d3-scale";
import { chartColors } from "src/components/theme/theme";
import {
  chartOptionBubbleChart,
  chartOptionColor,
  chartOptionSomeColumnCharts,
  chartOptionStack,
  chartOptionVisible,
} from "src/components/chart/options";
import { isNonNullable, isNumber } from "src/utils/types/types";
import {
  resultDate,
  resultFloat,
  resultString,
} from "src/utils/results/values";
import {
  scaleBand,
  scaleLinear,
  scaleLog,
  scaleOrdinal,
  scaleSqrt,
  scaleUtc,
} from "@visx/scale";

export interface ChartScales {
  x: ChartScale;
  z?: ChartScale;
  y0?: ChartScale;
  y1?: ChartScale;
  color: (value: string) => string;
}

export type ChartScale =
  | ScaleBand<string>
  | ScaleTime<number, number>
  | ScaleLinear<number, number>
  | ScalePower<number, number>;

export const createChartScales = (
  visual: VisualChart,
  series: ChartSeries,
  bounds: ChartBounds
): ChartScales => {
  const isVisible = (column: QueryResultColumn) =>
    chartOptionVisible(visual, column.name);

  const y0s = leftAxisColumns(series).filter(isVisible);
  const y1s = rightAxisColumns(series).filter(isVisible);
  const z = createScaleZ(visual, series, bounds);

  const x = createScaleX(visual, series, bounds);
  const y0 = createScaleY(visual, series, bounds, y0s, visual.options.yAxis[0]);

  // Update the x and y scales to take into account any bubbles whose
  // radius might extend past the bounds of the current x and y-axis.
  if (chartOptionBubbleChart(visual, z) && y0) {
    updateBubbleChartScales(visual, series, x, y0, z);
  }

  return {
    color: createColorScale(visual, series.ys),
    x,
    y0,
    y1: createScaleY(visual, series, bounds, y1s, visual.options.yAxis[1]),
    z,
  };
};

interface UpdateBubbleChartScales {
  x: ChartScale;
  y0?: ChartScale;
}

const updateBubbleChartScales = (
  _visual: VisualChart, // Unused
  series: ChartSeries,
  xScale: ChartScale,
  y0Scale: ChartScale,
  zScale: ChartScale
): UpdateBubbleChartScales => {
  const currentScales = {
    x: xScale,
    y0: y0Scale,
  };

  const scaleOffsets = {
    x: {
      offsets: { min: 0, max: 0 },
    },
    y: {
      offsets: { min: 0, max: 0 },
    },
  };

  // Circles in the scatter chart can overlap the chart borders.
  // Because of that we need to update x/y scale functions to fit the chart
  const updateScaleOffsets = (
    pixelPosition: number,
    zRadius: number,
    startRange: number,
    endRange: number,
    axis: "x" | "y"
  ) => {
    const isReversed = startRange > endRange;
    const minPosition = isReversed ? endRange : startRange;
    const maxPosition = isReversed ? startRange : endRange;

    // Find the biggest circle that is overlapping chart borders
    // For 'x' on the left and right
    // For 'y' on the top and bottom
    if (pixelPosition - zRadius < minPosition) {
      scaleOffsets[axis].offsets.min = Math.max(
        Math.abs(pixelPosition - zRadius),
        scaleOffsets[axis].offsets.min
      );
    } else if (pixelPosition + zRadius > maxPosition) {
      scaleOffsets[axis].offsets.max = Math.max(
        Math.abs(pixelPosition + zRadius - maxPosition),
        scaleOffsets[axis].offsets.max
      );
    }
  };

  // Iterate over rows and find the biggest overlapping circles by the z-axis
  // Add left,right,top,bottom offsets to fit all the circles
  series.rows.forEach((row) => {
    const zRadius = scaleAccessor(zScale, series.z)(row);
    const range = xScale.range();

    updateScaleOffsets(
      scaleAccessor(xScale, series.x)(row),
      zRadius,
      range[0],
      range[1],
      "x"
    );

    if (y0Scale) {
      const range = y0Scale.range();

      series.y0.scatter.forEach((column) => {
        if (typeof row[column.name] !== "undefined") {
          updateScaleOffsets(
            scaleAccessor(y0Scale, column)(row),
            zRadius,
            range[0],
            range[1],
            "y"
          );
        }
      });
    }
  });

  // Add offset to move the chart 'x' pixels from the left and subtract to move 'x' pixels from the right
  currentScales.x.range([
    xScale.range()[0] + scaleOffsets.x.offsets.min,
    xScale.range()[1] - scaleOffsets.x.offsets.max,
  ]);

  // Subtract offset to move the chart 'y' pixels higher and add to move 'y' pixels lower
  currentScales.y0.range([
    currentScales.y0.range()[0] - scaleOffsets.y.offsets.max,
    currentScales.y0.range()[1] + scaleOffsets.y.offsets.min,
  ]);

  return currentScales;
};

export const MIN_SCATTER_CHART_CIRCLE_RADIUS = 3;
export const SCATTER_CHART_CIRCLE_OPACITY = 0.8;

const createScaleZ = (
  visual: VisualChart,
  series: ChartSeries,
  bounds: ChartBounds
): ScalePower<number, number> | undefined => {
  if (chartOptionBubbleChart(visual, series.z)) {
    return createSqrtScale(
      visual,
      series.rows,
      [series.z],
      MIN_SCATTER_CHART_CIRCLE_RADIUS,
      // 10% of the minimal x,y length
      min([bounds.xMax, bounds.yMax])! * 0.1
    );
  }
};

const createScaleX = (
  visual: VisualChart,
  series: ChartSeries,
  bounds: ChartBounds
): ChartScale => {
  const someColumnCharts = chartOptionSomeColumnCharts(visual);
  const range: [number, number] = [0, bounds.xMax];
  const type = series.x.type;

  if (someColumnCharts || type === "string") {
    return createBandScale(visual, series, [series.x], range, "x");
  }

  if (type === "datetime") {
    return createTimeScale(visual, series, [series.x], range, "x");
  }

  if (visual.options.xAxis.type === "logarithmic") {
    return createLogScale(visual, series, [series.x], range, "x");
  }

  return createLinearScale(visual, series, [series.x], range, "x");
};

const createScaleY = (
  visual: VisualChart,
  series: ChartSeries,
  bounds: ChartBounds,
  columns: QueryResultColumn[],
  axis?: AxisOptions
): ChartScale | undefined => {
  const range: [number, number] = [bounds.yMax, 0];
  const type = columns[0]?.type;

  if (!axis || columns.length === 0) {
    return;
  }

  if (type === "string") {
    return createBandScale(visual, series, columns, range, "y");
  }

  if (type === "datetime") {
    return createTimeScale(visual, series, columns, range, "y");
  }

  if (axis.type === "logarithmic") {
    return createLogScale(visual, series, columns, range, "y");
  }

  // It's possible that a user selects Line chart when adding a visualization and then choose a different type of chart in the series
  // If the type selected in the series is not a line chart, it would still be treated as one if we didn't check the series options column type.
  if (visual.options.globalSeriesType === "line") {
    if (
      visual.options.seriesOptions?.[columns[0].name]?.type !== undefined &&
      visual.options.seriesOptions?.[columns[0].name]?.type !== "line"
    ) {
      return createZeroedLinearScale(visual, series, columns, range);
    } else {
      return createLinearScale(visual, series, columns, range, "y");
    }
  }

  return createZeroedLinearScale(visual, series, columns, range);
};

export const createSqrtScale = (
  visual: VisualChart,
  rows: QueryResultRow[],
  columns: [QueryResultColumn],
  minRange: number,
  maxRange: number
) => {
  const domain = domainLinear(visual, rows, columns, "z");
  // The range must be normalized so that the point value depends on the max domain value
  // If 'x1' point with value 10 has max domain value 100, then 'x2' point with value 5 has domain value 50
  const minRangeNormalized = domain[1] ? (domain[0] / domain[1]) * maxRange : 0;

  // Power scale is used to match surface area instead of length (Bubble Charts use z-axis as size of the surface)
  // Ex: If the value 'b' is 4 times bigger than value 'a', the result is only multiplied by 2
  return scaleSqrt({
    range: [Math.max(minRange, minRangeNormalized), maxRange],
    domain: domainLinear(visual, rows, columns, "z"),
  });
};

const createBandScale = (
  visual: VisualChart,
  series: ChartSeries,
  columns: QueryResultColumn[],
  range: [number, number],
  axis: "x" | "y"
) => {
  const reverse = axis === "x" && visual.options.reverseX;

  return scaleBand({
    domain: reverse
      ? allValues(series.rows, columns).map(resultString).reverse()
      : allValues(series.rows, columns).map(resultString),
    padding: 0.1,
    range,
  });
};

const createTimeScale = (
  visual: VisualChart,
  series: ChartSeries,
  columns: QueryResultColumn[],
  range: [number, number],
  axis: "x" | "y"
) => {
  const reverse = axis === "x" && visual.options.reverseX;

  return scaleUtc({
    domain: reverse
      ? domainTime(series.rows, columns).reverse()
      : domainTime(series.rows, columns),
    range,
  });
};

const createLogScale = (
  visual: VisualChart,
  series: ChartSeries,
  columns: QueryResultColumn[],
  range: [number, number],
  axis: "x" | "y"
) => {
  const reverse = axis === "x" && visual.options.reverseX;
  const domain = domainLinear(visual, series.rows, columns, axis);

  // If both ends of the domain are positive we can use the minValue for the scale.
  // Otherwise if the domain starts or ends at 0, it makes sense to use the minimum/maximum value that is different from 0.
  // As the documentation states:
  // log(0) = -∞, a log scale domain must be strictly-positive or strictly-negative. The domain must not include or cross zero.
  const minValue =
    domain[0] === 0
      ? min(allFloats(series.rows, columns).filter((n) => n > 0)) || 1
      : domain[0];

  const maxValue =
    domain[1] === 0
      ? max(allFloats(series.rows, columns).filter((n) => n < 0)) || -1
      : domain[1];

  return scaleLog({
    domain: reverse ? [minValue, maxValue].reverse() : [minValue, maxValue],
    range,
  });
};

const createLinearScale = (
  visual: VisualChart,
  series: ChartSeries,
  columns: QueryResultColumn[],
  range: [number, number],
  axis: "x" | "y"
) => {
  const reverse = axis === "x" && visual.options.reverseX;

  return scaleLinear({
    domain: reverse
      ? domainLinear(visual, series.rows, columns, axis).reverse()
      : domainLinear(visual, series.rows, columns, axis),
    range,
  });
};

const createZeroedLinearScale = (
  visual: VisualChart,
  series: ChartSeries,
  columns: QueryResultColumn[],
  range: [number, number]
) => {
  return scaleLinear({
    domain: domainLinearZeroed(visual, series.rows, columns),
    range,
  });
};

const createColorScale = (
  visual: VisualChart,
  columns: QueryResultColumn[]
) => {
  const colorScale = scaleOrdinal({
    domain: columns.map((c) => c.name),
    range: chartColors,
  });

  return (name: string) => {
    return chartOptionColor(visual, name) || colorScale(name);
  };
};

const domainTime = (
  rows: QueryResultRow[],
  columns: QueryResultColumn[]
): [number, number] => {
  const values = rows.flatMap((row) => rowValues(row, columns)).map(resultDate);
  return [min(values) || 0, max(values) || 0];
};

export const domainLinear = (
  visual: VisualChart,
  rows: QueryResultRow[],
  columns: QueryResultColumn[],
  axis: "x" | "y" | "z"
): [number, number] => {
  if (axis === "y" && chartOptionStack(visual)) {
    const { positive, negative } = getStackedPositiveNegativeValues(
      visual.options,
      columns,
      rows
    );

    return [min(negative) || 0, max(positive) || 0];
  } else {
    const values = allFloats(rows, columns);
    return [min(values) || 0, max(values) || 0];
  }
};

const domainLinearZeroed = (
  visual: VisualChart,
  rows: QueryResultRow[],
  columns: QueryResultColumn[]
): [number, number] => {
  const [min, max] = domainLinear(visual, rows, columns, "y");
  const minZeroed = Math.min(0, min);
  const maxZeroed = Math.max(0, max);
  return [minZeroed, maxZeroed];
};

// Create an accessor that calls a scale for a column and row combination.
// Each shape generator exposes accessors that control how the input data are mapped to a visual representation.
// For example, in a area, by default accessor assumes that the input data are two-element arrays of numbers,
// if data is in a different format, then a custom accessor is necessary.
export const scaleAccessor = (scale: ChartScale, c: QueryResultColumn) => {
  return (r: QueryResultRow): number => {
    if (isBandScale(scale)) {
      const offset = scale.bandwidth() / 2;
      const value = accessString(c)(r);
      return (scale(value) || 0) + offset;
    } else if (c.type === "datetime") {
      const value = accessDate(c)(r);
      return scale(value) || 0;
    } else {
      const value = accessFloat(c)(r);
      return scale(value) || 0;
    }
  };
};

// Some charts require all values to be non-null in order to render correctly.
// This helper replaces any null values with zeroes in numeric columns.
export const fillEmptyRows = (
  rs: QueryResultRow[],
  cs: QueryResultColumn[]
) => {
  return rs.map((r) => {
    const copy = { ...r };
    cs.forEach((c) => {
      if (c.type === "float" && !copy[c.name]) {
        copy[c.name] = 0;
      }
    });
    return copy;
  });
};

export const isBandScale = (scale: ChartScale): scale is ScaleBand<string> => {
  return "bandwidth" in scale;
};

export const nonEmptyRows = (rs: QueryResultRow[], cs: QueryResultColumn[]) => {
  return rs.filter((r) => cs.find((c) => isNonNullable(r[c.name])));
};

const allValues = (rs: QueryResultRow[], cs: QueryResultColumn[]) => {
  return rs.flatMap((r) => rowValues(r, cs));
};

export const allFloats = (rs: QueryResultRow[], cs: QueryResultColumn[]) => {
  return allValues(rs, cs).map(resultFloat);
};

export const positiveSums = (rs: QueryResultRow[], cs: QueryResultColumn[]) => {
  return rs.map((r) => sum(rowFloats(r, cs).filter((x) => x >= 0)));
};

const negativeSums = (rs: QueryResultRow[], cs: QueryResultColumn[]) => {
  return rs.map((r) => sum(rowFloats(r, cs).filter((x) => x < 0)));
};

const getMinValues = (rs: QueryResultRow[], cs: QueryResultColumn[]) => {
  return rs
    .map((r) => min(rowFloats(r, cs).filter((x) => x < 0)))
    .filter(isNumber);
};

const getMaxValues = (rs: QueryResultRow[], cs: QueryResultColumn[]) => {
  return rs
    .map((r) => max(rowFloats(r, cs).filter((x) => x >= 0)))
    .filter(isNumber);
};

const rowFloats = (r: QueryResultRow, cs: QueryResultColumn[]) => {
  return rowValues(r, cs).map(resultFloat);
};

export const rowValues = (r: QueryResultRow, cs: QueryResultColumn[]) => {
  return cs.map((c) => r[c.name]);
};

export const accessString = (c: QueryResultColumn) => {
  return (d: QueryResultRow): string => resultString(d[c.name]);
};

const accessDate = (c: QueryResultColumn) => {
  return (d: QueryResultRow): number => resultDate(d[c.name]);
};

export const accessFloat = (c: QueryResultColumn) => {
  return (d: QueryResultRow): number => resultFloat(d[c.name]);
};

export const accessDateOrFloat = (c: QueryResultColumn) => {
  return (d: QueryResultRow): number => {
    return c.type === "datetime"
      ? resultDate(d[c.name])
      : resultFloat(d[c.name]);
  };
};

/**
 * __getColumnsByType__
 * Function splits columns to groups by series chartType.
 * @param options
 * @param columns
 *
 * @returns {Record<ChartType, QueryResultColumn[]>}  columns divided by series chartType
 */
export const getColumnsByType = (
  options: VisualChart["options"],
  columns: QueryResultColumn[]
) => {
  return columns.reduce(
    (acc, column) => {
      const { name } = column;
      const type =
        (options.seriesOptions &&
          name in options.seriesOptions &&
          options.seriesOptions[name].type) ||
        options.globalSeriesType;

      if (!acc[type]) {
        acc[type] = [];
      }

      acc[type].push(column);

      return acc;
    },
    {} as Record<ChartType, QueryResultColumn[]>
  );
};

interface GetStackedPositiveNegativeValues {
  positive: number[];
  negative: number[];
}

/**
 * Function computes negative and positive arrays to resolve min/max of the 'y' axis
 * Stackable series chartTypes use `sum` to resolve min/max
 * Non-stackable series charTypes use min/max value
 *
 * When stacking values of different labels, all the negative values are stacked below 0 and all the positive values are stacked above zero.
 * Therefore, we need to calculate the sums of the negative and positive values separately.
 *
 * @param options
 * @param columns
 * @param rows
 *
 * @returns {GetStackedPositiveNegativeValues} positive and negative values
 */
export const getStackedPositiveNegativeValues = (
  options: VisualChart["options"],
  columns: QueryResultColumn[],
  rows: QueryResultRow[]
) => {
  const columnsByType = getColumnsByType(options, columns);

  return Object.entries(columnsByType).reduce<GetStackedPositiveNegativeValues>(
    (acc, [chartType, columns]) => {
      const isStackable = ["column", "area"].includes(chartType);

      acc.positive = acc.positive.concat(
        isStackable ? positiveSums(rows, columns) : getMaxValues(rows, columns)
      );
      acc.negative = acc.negative.concat(
        isStackable ? negativeSums(rows, columns) : getMinValues(rows, columns)
      );

      return acc;
    },
    {
      positive: [],
      negative: [],
    }
  );
};
